<template>
  <div class="card card-custom gutter-b" v-loading="loading">
    <div class="card-header d-flex justify-content-between">
      <div class="card-title">
        <h3 class="card-label">Danh sách mã vạch</h3>
      </div>
      <div class="card-title">
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="!productList.length"
          @click="handlePrint"
        >
          <i class="fas fa-print"></i> In barcode
        </button>
      </div>
    </div>

    <div class="card-body">
      <div class="row filter_form">
        <div class="col-md-3">
          <div class="form-group">
            <el-input
              v-model="searchQuery"
              placeholder="Tìm kiếm tên mặt hàng"
              clearable
              @input="handleSearchInput"
              @keyup.enter.native="getProductList"
              @clear="getProductList"
            >
              <i slot="prefix" class="el-input__icon el-icon-search"></i>
            </el-input>
          </div>
        </div>

        <div class="col-md-3">
          <div class="form-group">
            <el-select
              v-model="selectedType"
              class="w-100"
              clearable
              filterable
              placeholder="Chọn loại"
              @change="getProductList"
            >
              <el-option
                v-for="item in productTypeOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </div>
        </div>

        <div class="col-md-3">
          <div class="form-group">
            <el-select
              v-model="selectedTypeDetail"
              class="w-100"
              clearable
              filterable
              placeholder="Chọn phân loại chi tiết"
              @change="getProductList"
            >
              <el-option
                v-for="item in productTypeDetailOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </div>
        </div>

        <div class="col-md-3">
          <div class="form-group">
            <el-select
              v-model="selectedReorderLevel"
              class="w-100"
              clearable
              placeholder="Trạng thái"
              @change="getProductList"
            >
              <el-option
                v-for="item in reorderLevelOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </div>
        </div>
      </div>

      <div class="table-responsive mt-4">
        <table class="table table-vertical-center table-bordered mb-0">
          <thead class="bg-light">
            <tr>
              <th class="text-left align-middle table-stt">STT</th>
              <th class="text-left align-middle">Tên mặt hàng</th>
              <th class="text-left align-middle table-barcode">Mã vạch (Barcode)</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="productList.length > 0">
              <tr v-for="(item, index) of productList" :key="item.id">
                <td class="text-left align-middle">{{ index + 1 }}</td>
                <td class="text-left align-middle">
                  <div class="d-flex align-items-center">
                    <p class="line-clamp-1 mb-0 text-break">{{ item.name }}</p>
                    <i
                      v-if="isNeedReorder(item)"
                      class="fas fa-exclamation-triangle reorder-warning-icon ml-2"
                      title="Sản phẩm cần mua"
                    ></i>
                  </div>
                </td>
                <td class="text-left align-middle">
                  <svg :ref="`barcode-${item.id}`" class="barcode-svg"></svg>
                </td>
              </tr>
            </template>

            <template v-else>
              <tr>
                <td colspan="3" class="text-center">Chưa có dữ liệu</td>
              </tr>
            </template>
          </tbody>
        </table>

        <div v-if="productList.length > 0" class="d-flex justify-content-end mt-4">
          <div>
            Tổng số bản ghi:
            <span class="ml-1 text-primary font-weight-bold">{{ productList.length }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import JsBarcode from "jsbarcode";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  PRODUCT_TYPE_DETAIL_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
} from "@/core/option/logisticsOption";
import { GET_PRODUCT_BARCODES } from "@/core/services/store/logistics/logistics.module";

const BARCODE_OPTIONS = {
  format: "CODE128",
  width: 1.2,
  height: 32,
  margin: 0,
  fontSize: 12,
  textMargin: 1,
  displayValue: true,
};

export default {
  name: "BarcodePrint",
  data() {
    return {
      loading: false,
      productList: [],
      searchQuery: "",
      selectedType: null,
      selectedTypeDetail: null,
      selectedReorderLevel: null,
      productTypeOptions: PRODUCT_TYPE_OPTIONS,
      productTypeDetailOptions: PRODUCT_TYPE_DETAIL_OPTIONS,
      reorderLevelOptions: [
        { value: 1, label: "Cần mua" },
        { value: 0, label: "Tất cả" },
      ],
      searchTimeout: null,
    };
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Logistics", route: "" },
      { title: "In Barcode" },
    ]);
    this.getProductList();
  },
  methods: {
    handleSearchInput() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(this.getProductList, 300);
    },
    async getProductList() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
      this.loading = true;
      try {
        const res = await this.$store.dispatch(GET_PRODUCT_BARCODES, {
          name: (this.searchQuery || "").trim(),
          type: this.selectedType,
          type_detail: this.selectedTypeDetail,
          reorder_level: this.selectedReorderLevel,
        });
        if (res?.error) throw new Error(res.message || "Không thể tải danh sách mã vạch");
        this.productList = (res.data ?? []).filter((item) => this.canRenderBarcode(item.barcode));
        this.$nextTick(this.renderBarcodes);
      } catch (e) {
        this.$message.error(e?.message || "Không thể tải danh sách mã vạch");
      } finally {
        this.loading = false;
      }
    },
    canRenderBarcode(barcode) {
      const value = String(barcode ?? "").trim();
      if (!value) return false;
      try {
        JsBarcode(document.createElementNS("http://www.w3.org/2000/svg", "svg"), value, BARCODE_OPTIONS);
        return true;
      } catch (error) {
        return false;
      }
    },
    renderBarcodes() {
      this.productList.forEach((item) => {
        const ref = this.$refs[`barcode-${item.id}`];
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (!el) return;
        try {
          JsBarcode(el, String(item.barcode).trim(), BARCODE_OPTIONS);
        } catch (error) {
          console.error("Error rendering product barcode", item.barcode, error);
        }
      });
    },
    isNeedReorder(item) {
      return Number(item?.quantity) < Number(item?.reorder_level);
    },
    escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    handlePrint() {
      const rows = this.productList
        .map((item, index) => {
          const ref = this.$refs[`barcode-${item.id}`];
          const el = Array.isArray(ref) ? ref[0] : ref;
          return `<tr>
            <td>${index + 1}</td>
            <td>${this.escapeHtml(item.name)}</td>
            <td class="barcode">${el ? el.outerHTML : this.escapeHtml(item.barcode)}</td>
          </tr>`;
        })
        .join("");

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        this.$message.warning("Trình duyệt đang chặn cửa sổ in, vui lòng cho phép popup");
        return;
      }
      printWindow.document.write(`<!doctype html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>In Barcode</title>
            <style>
              body { font-family: Arial, sans-serif; font-size: 13px; margin: 16px; }
              table { width: 100%; border-collapse: collapse; }
              th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; vertical-align: middle; }
              th { background: #f3f6f9; }
              tr { page-break-inside: avoid; }
              td.barcode { text-align: center; width: 40%; }
            </style>
          </head>
          <body>
            <table>
              <thead><tr><th>STT</th><th>Tên mặt hàng</th><th>Mã vạch (Barcode)</th></tr></thead>
              <tbody>${rows}</tbody>
            </table>
          </body>
        </html>`);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 300);
    },
  },
  beforeDestroy() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  },
};
</script>

<style scoped>
.table-stt {
  width: 5%;
}

.table-barcode {
  width: 35%;
}

.filter_form .form-group {
  margin-bottom: 16px;
}

.barcode-svg {
  display: block;
  margin: 0 auto;
}

.reorder-warning-icon {
  color: #ff8a1f;
  flex-shrink: 0;
  font-size: 1rem;
}
</style>
